import React, { useEffect, useState } from 'react'
import client from '../../client/Client'
import HeaderBanner from '../ui/HeaderBanner'

interface content {
  HotelName: string,
  HotelImage: {
    asset: {
      _ref: string
    }
  },
  HotelInformation: infoType[],
  MoreHotelIMG?: Array<{
    _key: string;
    _type: string;
    asset: {
      _ref: string;
      _type: string;
    }
  }>
}

interface infoType {
  _key?: string;
  _type?: string;
  style?: string;
  markDefs: Array<{
    _key: string;
    _type: string;
    href?: string;
  }>;
  children: Array<{
    _key?: string;
    _type?: string;
    marks?: string[];
    text: string;
  }>;
}


const HotelInfo = () => {
  const [data, setData] = useState<any>();
  const [HotelInfo, setHotelInfo] = useState<content[]>([])
  const [moreHotelImages, setMoreHotelImages] = useState<Array<{
    _key: string;
    _type: string;
    asset: {
      _ref: string;
      _type: string;
    }
  }>>([])

  useEffect(() => {
    client.fetch('*[_type == "AccomodationANDRegistration"]')
      .then((result) => {
        setData(result[0]);
        setHotelInfo(result[0].HotelInformations)
        setMoreHotelImages(result[0].MoreHotelIMG || [])
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [])

  // Function to generate image URL from Sanity asset reference
  const generateImageUrl = (assetRef: string) => {
    if (!assetRef) return "";
    return `https://cdn.sanity.io/images/79vgbaej/production/${assetRef.slice(6, -4)}.${assetRef.slice(-3)}`;
  }
  return (
    <>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
        <div className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1" aria-hidden="true">
          <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
                <stop stopColor="#FFF" offset="0%" />
                <stop stopColor="#EAEAEA" offset="77.402%" />
                <stop stopColor="#DFDFDF" offset="100%" />
              </linearGradient>
            </defs>
            <g fill="url(#illustration-01)" fillRule="evenodd">
              <circle cx="1232" cy="128" r="128" />
              <circle cx="155" cy="443" r="64" />
            </g>
          </svg>
        </div>
        
        {/* Header Banner */}
        <HeaderBanner title='Hotel Information' />
        
        {/* Hotel Information Cards */}
        <div className="text-left space-y-8 pt-12">
          {HotelInfo.length > 0 && HotelInfo.map((item, index) => {
            
            const imageUrl = item.HotelImage?.asset?._ref
              ? generateImageUrl(item.HotelImage.asset._ref)
              : "";
              
            return (
              <div key={index} className="bg-gray-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm">
                {/* Hotel Name Header with Separating Line */}
                <div className="mb-6 pb-4 border-b border-gray-500">
                  <h2 className="text-2xl font-bold leading-tight tracking-tight text-red-700">{item.HotelName}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Hotel Information */}
                  <div>
                    <div className="space-y-3">
                      {item?.HotelInformation?.length > 0 &&
                        item.HotelInformation.map((info, idx) => (
                          <div key={idx} className="flex items-start">
                            {/* Red bullet */}
                            <div className="flex-shrink-0 mt-1 mr-3">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            </div>

                            {/* Text content with links + formatting */}
                            <p className="text-gray-700 leading-relaxed">
                              {info.children.map((child: any, childIdx: number) => {
                                // Find link data if a mark key matches
                                const linkData =
                                  child.marks && child.marks.length > 0
                                    ? info.markDefs.find(
                                        (def: any) => def._key === child.marks[0]
                                      )
                                    : null;

                                // Determine if "strong" or "underline" marks exist
                                const hasStrong = child.marks?.includes("strong");
                                const hasUnderline = child.marks?.includes("underline");

                                // Compute styles for text formatting
                                let textClasses = "";
                                if (hasStrong && hasUnderline) {
                                  // both: bold + red (no underline)
                                  textClasses = "font-semibold text-red-600";
                                } else if (hasStrong) {
                                  // only bold
                                  textClasses = "font-semibold";
                                } else if (hasUnderline) {
                                  // only underline
                                  textClasses = "underline";
                                }

                                // Styled text span
                                const content = (
                                  <span key={childIdx} className={textClasses}>
                                    {child.text}
                                  </span>
                                );

                                // If link exists, wrap the styled text in <a>
                                if (linkData) {
                                  return (
                                    <a
                                      key={childIdx}
                                      href={linkData.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-901 hover:text-blue-800 font-medium underline transition-colors duration-200"
                                    >
                                      {content}
                                    </a>
                                  );
                                }

                                // Otherwise, just render the styled text
                                return content;
                              })}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>


                  
                  {/* Hotel Image */}
                  <div>
                    {imageUrl && (
                      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img 
                          className="w-full h-full object-cover" 
                          src={imageUrl} 
                          alt={item.HotelName} 
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* More Hotel Images Section */}
        {moreHotelImages && moreHotelImages.length > 0 && (
          <div className="mt-12">
            <div className="text-center mb-8">
              {/* You can add a title here if needed */}
            </div>
            
            {/* Wider grid with full-width images */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {moreHotelImages.map((imageItem, index) => {
                const imageUrl = imageItem.asset?._ref
                  ? generateImageUrl(imageItem.asset._ref)
                  : "";
                
                return (
                  <div 
                    key={imageItem._key || index} 
                    className="w-full overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1"
                  >
                    <div className="relative w-full h-80 sm:h-96 lg:h-[500px]">
                      <img 
                        className="w-full h-full object-cover"
                        src={imageUrl} 
                        alt={`Hotel view ${index + 1}`}
                        loading="lazy"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        
        {/* Accommodation Form Button - Conditionally rendered */}
        {data?.AccomodationForm && (
          <div className="mt-16 mb-12 text-center">
            <a 
              href={data.AccomodationForm} 
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-901 text-white font-semibold border border-blue-901 hover:border-blue-901 rounded-lg shadow-md hover:bg-white hover:text-blue-901 transition-colors duration-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
              </svg>
              Accommodation Form
            </a>
          </div>
        )}
      </div>
    </>
  )
}

export default HotelInfo